import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faSpinnerThird, faUnlock, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { toast } from "react-toastify";
import NotificationToast from "../Helpers/NotificationToast";
import { useSelector } from "react-redux";

const RogersStuff = ({ current_booking, current_staff, refreshBooking }) => {
    const [tenantType, setTenantType] = useState(current_booking?.tenant_type || "");
    const [isRenewal, setIsRenewal] = useState(current_booking?.is_renewal || false);
    const [paymentStaging, setPaymentStaging] = useState(current_booking?.payment_staging || false);
    const [bypassCollections, setBypassCollections] = useState(current_booking?.bypass_collections || false);
    const [saving, setSaving] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [clearingUnsub, setClearingUnsub] = useState(false);
    const [clearingFileType, setClearingFileType] = useState(false);
    const [deletingFiles, setDeletingFiles] = useState(false);
    const [clearingMoveInOut, setClearingMoveInOut] = useState(false);
    const [message, setMessage] = useState("");
    const [useMessagebird, setUseMessagebird] = useState(!!current_booking?.use_messagebird);
    const [mbToggleLoading, setMbToggleLoading] = useState(false);

    // Delete / Move messages state
    const { settings } = useSelector((state) => state.settings);
    const [historicBookings, setHistoricBookings] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showMoveMessagesModal, setShowMoveMessagesModal] = useState(false);
    const [moveMessagesData, setMoveMessagesData] = useState({ fromYearId: "", toYearId: "", startDate: "", endDate: "" });
    const [messageCount, setMessageCount] = useState(null);
    const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
    const [years, setYears] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        loadBookingHistory();
        // load years for move messages
        axios.get(`${process.env.REACT_APP_API_URL}years`).then((res) => setYears(res.data || [])).catch(() => {});
    }, [current_booking?.id]);

    const loadBookingHistory = async () => {
        try {
            const response = await axios({ method: "post", url: `${process.env.REACT_APP_API_URL}booking_history`, data: { user_id: current_booking?.user?.id } });
            setHistoricBookings(response.data || []);
        } catch (err) {
            console.log(err);
        }
    };

    async function deleteAllMessagesForBooking(bookingId) {
        if (!bookingId) return;
        if (!window.confirm("This will permanently delete ALL messages for this booking. Continue?")) return;
        setActionLoading(true);
        try {
            const res = await axios({ method: "delete", url: `${process.env.REACT_APP_API_URL}messages/booking/${bookingId}` });
            toast(<NotificationToast title="Messages Deleted" message={`Deleted ${res?.data?.deleted ?? 0} messages for this booking.`} />);
            await loadBookingHistory();
        } catch (error) {
            toast(<NotificationToast title="Delete failed" message={error?.response?.data?.message || "Could not delete messages"} type="error" />);
        } finally {
            setActionLoading(false);
        }
    }

    async function deleteSingleBooking(bookingId) {
        if (!bookingId) return;
        if (!window.confirm("This will permanently delete the booking and all related data (messages, files, AOS, appointment links, etc.).\n\nAre you sure you want to proceed?")) return;
        setActionLoading(true);
        try {
            await axios({ method: "delete", url: `${process.env.REACT_APP_API_URL}delete_booking_perm/${bookingId}`, data: { current_staff } });
            toast(<NotificationToast title="Booking Deleted" message="The booking was deleted successfully." />);
            await loadBookingHistory();
        } catch (error) {
            toast(<NotificationToast title="Delete failed" message={error?.response?.data?.error || "Could not delete booking"} type="error" />);
        } finally {
            setActionLoading(false);
        }
    }

    async function deleteUserAllBookings(userId) {
        if (!userId) return;
        if (!window.confirm("This will delete the user and all of their bookings. Continue?")) return;
        setActionLoading(true);
        try {
            await axios({ method: "delete", url: `${process.env.REACT_APP_API_URL}delete_user_perm/${userId}`, data: { current_staff } });
            toast(<NotificationToast title="User Deleted" message="The user and their bookings were deleted." />);
            await loadBookingHistory();
        } catch (error) {
            toast(<NotificationToast title="Delete failed" message={error?.response?.data?.error || "Could not delete user"} type="error" />);
        } finally {
            setActionLoading(false);
        }
    }

    async function checkMessageCount() {
        if (!moveMessagesData.fromYearId || !moveMessagesData.toYearId) {
            toast(<NotificationToast title="Validation Error" message="Please select both From Year and To Year" type="error" />);
            return;
        }
        if (moveMessagesData.fromYearId === moveMessagesData.toYearId) {
            toast(<NotificationToast title="Validation Error" message="From Year and To Year must be different" type="error" />);
            return;
        }
        if (moveMessagesData.startDate && moveMessagesData.endDate && moveMessagesData.startDate > moveMessagesData.endDate) {
            toast(<NotificationToast title="Validation Error" message="Start date must be before end date" type="error" />);
            return;
        }
        setActionLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}messages/count`,
                data: {
                    user_id: current_booking?.user?.id,
                    from_year_id: parseInt(moveMessagesData.fromYearId),
                    to_year_id: parseInt(moveMessagesData.toYearId),
                    start_date: moveMessagesData.startDate || null,
                    end_date: moveMessagesData.endDate || null,
                },
            });
            setMessageCount(res?.data?.count ?? 0);
            setShowMoveConfirmation(true);
        } catch (error) {
            toast(<NotificationToast title="Count failed" message={error?.response?.data?.message || "Could not count messages"} type="error" />);
        } finally {
            setActionLoading(false);
        }
    }

    async function moveMessages() {
        setActionLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}messages/move`,
                data: {
                    user_id: current_booking?.user?.id,
                    from_year_id: parseInt(moveMessagesData.fromYearId),
                    to_year_id: parseInt(moveMessagesData.toYearId),
                    start_date: moveMessagesData.startDate || null,
                    end_date: moveMessagesData.endDate || null,
                },
            });
            toast(<NotificationToast title="Messages Moved" message={`Moved ${res?.data?.moved ?? 0} messages successfully.`} />);
            setShowMoveMessagesModal(false);
            setShowMoveConfirmation(false);
            setMessageCount(null);
            setMoveMessagesData({ fromYearId: "", toYearId: "", startDate: "", endDate: "" });
            await loadBookingHistory();
        } catch (error) {
            toast(<NotificationToast title="Move failed" message={error?.response?.data?.message || "Could not move messages"} type="error" />);
        } finally {
            setActionLoading(false);
        }
    }

    const handleSave = async () => {
        setSaving(true);
        setMessage("");

        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/admin`, {
                tenant_type: tenantType,
                is_renewal: isRenewal,
                payment_staging: paymentStaging,
                bypass_collections: bypassCollections,
            });
            setMessage("Saved successfully!");
            if (refreshBooking) {
                refreshBooking();
            }
        } catch (error) {
            console.error("Error updating fields:", error);
            setMessage("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const handleClearBlock = async () => {
        if (!window.confirm("Are you sure you want to clear the 48-hour experiment block for this booking?")) {
            return;
        }

        setClearing(true);
        setMessage("");

        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/admin`, {
                clear_experiment_block: true,
            });
            setMessage("Experiment block cleared successfully!");
            if (refreshBooking) {
                refreshBooking();
            }
        } catch (error) {
            console.error("Error clearing block:", error);
            setMessage("Failed to clear block");
        } finally {
            setClearing(false);
        }
    };

    const handleClearEmailUnsubscribe = async () => {
        if (!window.confirm("Are you sure you want to re-subscribe this user to email? They previously opted out — only do this if you've confirmed with them directly.")) {
            return;
        }

        setClearingUnsub(true);
        setMessage("");

        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/admin`, {
                clear_email_unsubscribe: true,
            });
            setMessage("Email unsubscribe cleared — user can now receive email.");
            if (refreshBooking) {
                refreshBooking();
            }
        } catch (error) {
            console.error("Error clearing email unsubscribe:", error);
            setMessage("Failed to clear email unsubscribe");
        } finally {
            setClearingUnsub(false);
        }
    };

    const handleClearFileType = async () => {
        if (!window.confirm("Are you sure you want to clear the file_type_id_to_upload for this booking?")) {
            return;
        }

        setClearingFileType(true);
        setMessage("");

        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/admin`, {
                file_type_id_to_upload: null,
            });
            setMessage("File type ID cleared successfully!");
            if (refreshBooking) {
                refreshBooking();
            }
        } catch (error) {
            console.error("Error clearing file type:", error);
            setMessage("Failed to clear file type");
        } finally {
            setClearingFileType(false);
        }
    };

    const handleDeleteAllFiles = async () => {
        if (
            !window.confirm(
                "⚠️ WARNING: This will permanently delete ALL uploaded files for this booking from both the database and Cloudflare storage. This cannot be undone!\n\nAre you sure you want to continue?",
            )
        ) {
            return;
        }

        setDeletingFiles(true);
        setMessage("");

        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/files`);
            const { deleted_count, errors } = response.data;

            if (errors && errors.length > 0) {
                setMessage(`Deleted ${deleted_count} files with ${errors.length} errors`);
            } else {
                setMessage(`Successfully deleted ${deleted_count} files`);
            }

            if (refreshBooking) {
                refreshBooking();
            }
        } catch (error) {
            console.error("Error deleting files:", error);
            setMessage("Failed to delete files: " + (error.response?.data?.message || error.message));
        } finally {
            setDeletingFiles(false);
        }
    };

    const handleClearMoveInOut = async () => {
        if (
            !window.confirm(
                "⚠️ WARNING: This will clear all move in/out checkboxes, dates, and permanently delete all inspection PDF documents. This cannot be undone!\n\nAre you sure you want to continue?",
            )
        ) {
            return;
        }

        setClearingMoveInOut(true);
        setMessage("");

        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/admin`, {
                clear_move_in_out_data: true,
            });
            setMessage("Move in/out data and inspection PDFs cleared successfully!");
            if (refreshBooking) {
                refreshBooking();
            }
        } catch (error) {
            console.error("Error clearing move in/out data:", error);
            setMessage("Failed to clear move in/out data: " + (error.response?.data?.message || error.message));
        } finally {
            setClearingMoveInOut(false);
        }
    };

    return (
        <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Admin Tools</h3>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tenant Type</label>
                    <input
                        type="text"
                        value={tenantType}
                        onChange={(e) => setTenantType(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                        disabled={saving}
                    />
                </div>

                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="is_renewal"
                        checked={isRenewal}
                        onChange={(e) => setIsRenewal(e.target.checked)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        disabled={saving}
                    />
                    <label htmlFor="is_renewal" className="ml-2 block text-sm font-medium text-gray-700">
                        Is Renewal
                    </label>
                </div>

                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="payment_staging"
                        checked={paymentStaging}
                        onChange={(e) => setPaymentStaging(e.target.checked)}
                        className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                        disabled={saving}
                    />
                    <label htmlFor="payment_staging" className="ml-2 block text-sm font-medium text-gray-700">
                        Payment Staging Mode
                    </label>
                    {paymentStaging && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">TEST</span>}
                </div>

                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="bypass_collections"
                        checked={bypassCollections}
                        onChange={(e) => setBypassCollections(e.target.checked)}
                        className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-300 rounded"
                        disabled={saving}
                    />
                    <label htmlFor="bypass_collections" className="ml-2 block text-sm font-medium text-gray-700">
                        Bypass Collections Tasks
                    </label>
                    {bypassCollections && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">BYPASSED</span>}
                </div>
                <p className="text-xs text-gray-500 -mt-2">
                    When enabled, this booking will be completely excluded from all collection automation tasks (WiFi cuts, LODs, Building Access).
                </p>

                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                    {saving ? (
                        <>
                            <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="mr-2" />
                            Save
                        </>
                    )}
                </button>

                {current_booking?.experiment_block_until && (
                    <button
                        onClick={handleClearBlock}
                        disabled={clearing}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                        {clearing ? (
                            <>
                                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                                Clearing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faUnlock} className="mr-2" />
                                Clear 48hr Block
                            </>
                        )}
                    </button>
                )}

                {current_booking?.user?.email_unsubscribed_at && (
                    <button
                        onClick={handleClearEmailUnsubscribe}
                        disabled={clearingUnsub}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 ml-2"
                        title={`User unsubscribed on ${current_booking.user.email_unsubscribed_at}`}
                    >
                        {clearingUnsub ? (
                            <>
                                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                                Clearing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faUnlock} className="mr-2" />
                                Clear Email Unsubscribe
                            </>
                        )}
                    </button>
                )}

                <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">File Type ID to Upload</label>
                            <p className="text-sm text-gray-900">
                                {current_booking?.file_type_id_to_upload !== null && current_booking?.file_type_id_to_upload !== undefined ? (
                                    current_booking.file_type_id_to_upload
                                ) : (
                                    <span className="text-gray-400 italic">Not set</span>
                                )}
                            </p>
                        </div>
                        {current_booking?.file_type_id_to_upload !== null && current_booking?.file_type_id_to_upload !== undefined && (
                            <button
                                onClick={handleClearFileType}
                                disabled={clearingFileType}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                            >
                                {clearingFileType ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                                        Clearing...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                        Clear
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                <div className="border-t pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clear Move In/Out Data (Testing)</label>
                    <p className="text-xs text-gray-500 mb-2">Clears move in/out checkboxes, dates, and deletes all inspection PDF documents.</p>
                    <button
                        onClick={handleClearMoveInOut}
                        disabled={clearingMoveInOut}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                        {clearingMoveInOut ? (
                            <>
                                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                                Clearing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                Clear Move In/Out Data
                            </>
                        )}
                    </button>
                </div>

                <div className="border-t pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Delete All Files (Testing)</label>
                    <p className="text-xs text-gray-500 mb-2">Permanently removes all uploaded files for this booking from database and Cloudflare storage.</p>
                    <button
                        onClick={handleDeleteAllFiles}
                        disabled={deletingFiles}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                        {deletingFiles ? (
                            <>
                                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                Delete All Files
                            </>
                        )}
                    </button>
                </div>

                <div className="border-t pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Messagebird / Meta Override</label>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <button
                                type="button"
                                disabled={mbToggleLoading}
                                onClick={async () => {
                                    if (mbToggleLoading) return;
                                    setMbToggleLoading(true);
                                    try {
                                        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}toggle_messagebird`, {
                                            booking_id: current_booking.id,
                                            staff_id: current_staff?.id,
                                        });
                                        setUseMessagebird(!!data.use_messagebird);
                                        if (refreshBooking) refreshBooking();
                                    } catch (e) {
                                        console.error("Toggle messagebird failed", e);
                                        setMessage("Failed to toggle Messagebird");
                                    } finally {
                                        setMbToggleLoading(false);
                                    }
                                }}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                    useMessagebird ? "bg-orange-500" : "bg-gray-300"
                                } ${mbToggleLoading ? "opacity-50" : ""}`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                        useMessagebird ? "translate-x-5" : "translate-x-0"
                                    }`}
                                />
                            </button>
                            <span className="text-sm text-gray-600">
                                {useMessagebird ? "Messagebird (old number)" : "Meta (new number)"}
                            </span>
                        </div>
                        {current_booking?.last_inbound_provider === "messagebird" && (
                            <span className="text-xs text-amber-600">Last reply from old number</span>
                        )}
                    </div>
                </div>

                {message && <p className={`text-sm ${message.includes("success") ? "text-green-600" : "text-red-600"}`}>{message}</p>}

                {/* Delete / Move Messages */}
                <div className="border-t pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Delete / Move</label>
                    <div className="flex flex-col gap-2">
                        <button onClick={() => setShowDeleteModal(true)} className="bg-red-700 text-white px-4 py-2 rounded text-sm uppercase w-full">
                            Delete User/Booking
                        </button>
                        <button onClick={() => deleteAllMessagesForBooking(current_booking?.id)} className="bg-red-600 text-white px-4 py-2 rounded text-sm uppercase w-full">
                            Delete All Messages
                        </button>
                        <button onClick={() => setShowMoveMessagesModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded text-sm uppercase w-full">
                            Move Messages
                        </button>
                    </div>
                </div>
            </div>

            {showDeleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg p-5 w-full max-w-sm shadow-xl">
                        <h3 className="text-base font-semibold mb-3">Delete Options</h3>
                        <p className="text-sm text-gray-600 mb-4">Choose what to delete for this user.</p>
                        <div className="flex flex-col gap-2">
                            {Array.isArray(historicBookings) && historicBookings.length > 1 && (
                                <button
                                    onClick={async () => {
                                        await deleteSingleBooking(current_booking?.id);
                                        setShowDeleteModal(false);
                                    }}
                                    className="bg-red-600 text-white px-4 py-2 rounded text-sm uppercase"
                                >
                                    Delete Booking
                                </button>
                            )}
                            <button
                                onClick={async () => {
                                    await deleteUserAllBookings(current_booking?.user?.id);
                                    setShowDeleteModal(false);
                                }}
                                className="bg-gray-800 text-white px-4 py-2 rounded text-sm uppercase"
                            >
                                Delete User (All)
                            </button>
                            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 rounded text-sm border mt-1">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showMoveMessagesModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
                        <h3 className="text-lg font-semibold mb-4">Move Messages</h3>
                        <p className="text-sm text-gray-600 mb-4">Move messages from one year to another with optional date range filters.</p>

                        {!showMoveConfirmation && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">From Year *</label>
                                    <select
                                        value={moveMessagesData.fromYearId}
                                        onChange={(e) => {
                                            setMoveMessagesData({ ...moveMessagesData, fromYearId: e.target.value });
                                            setShowMoveConfirmation(false);
                                            setMessageCount(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select Year</option>
                                        {Array.isArray(years) &&
                                            years.map((year) => (
                                                <option key={year.id || year.value} value={year.id || year.value}>
                                                    {year.label || year.value}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">To Year *</label>
                                    <select
                                        value={moveMessagesData.toYearId}
                                        onChange={(e) => {
                                            setMoveMessagesData({ ...moveMessagesData, toYearId: e.target.value });
                                            setShowMoveConfirmation(false);
                                            setMessageCount(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select Year</option>
                                        {Array.isArray(years) &&
                                            years.map((year) => (
                                                <option key={year.id || year.value} value={year.id || year.value}>
                                                    {year.label || year.value}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (Optional)</label>
                                    <input
                                        type="date"
                                        value={moveMessagesData.startDate}
                                        onChange={(e) => {
                                            setMoveMessagesData({ ...moveMessagesData, startDate: e.target.value });
                                            setShowMoveConfirmation(false);
                                            setMessageCount(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
                                    <input
                                        type="date"
                                        value={moveMessagesData.endDate}
                                        onChange={(e) => {
                                            setMoveMessagesData({ ...moveMessagesData, endDate: e.target.value });
                                            setShowMoveConfirmation(false);
                                            setMessageCount(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        )}

                        {!showMoveConfirmation ? (
                            <div className="flex gap-2 mt-6">
                                <button
                                    onClick={checkMessageCount}
                                    disabled={actionLoading}
                                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded text-sm uppercase hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {actionLoading ? "Checking..." : "Check Messages"}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowMoveMessagesModal(false);
                                        setShowMoveConfirmation(false);
                                        setMessageCount(null);
                                        setMoveMessagesData({ fromYearId: "", toYearId: "", startDate: "", endDate: "" });
                                    }}
                                    disabled={actionLoading}
                                    className="flex-1 px-4 py-2 rounded text-sm border hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <div className="mt-6">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                    <p className="text-center text-lg font-semibold text-blue-900">
                                        {messageCount} {messageCount === 1 ? "message" : "messages"} found
                                    </p>
                                    <p className="text-center text-sm text-blue-700 mt-1">Ready to move to selected year</p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={moveMessages}
                                        disabled={actionLoading}
                                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded text-sm uppercase hover:bg-green-700 disabled:opacity-50"
                                    >
                                        {actionLoading ? "Moving..." : "Proceed"}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowMoveConfirmation(false);
                                            setMessageCount(null);
                                        }}
                                        disabled={actionLoading}
                                        className="flex-1 px-4 py-2 rounded text-sm border hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Back
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RogersStuff;
